import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ModalNext } from '@jutro/components/widgets/modal-next/ModalNext';
import { ModalFooter } from '@jutro/components/widgets/modal-next/ModalFooter/ModalFooter';
import cx from 'classnames';
import { Flex, FlexItem } from '@jutro/layout';
import { useTranslator } from '@jutro/locale';
import { publish } from '@jutro/events';
import { WMICDocumentDownloadButton } from 'gw-capability-document-react';
import { WMICButton } from 'wmic-components-platform-react';
import { CONSTANTS, WMICTopics } from 'wmic-portals-utils-js';
import { PolicyService } from 'gw-capability-policy';
import pdfImg from 'wmic-amp-digital-theme-styles/images/policy/poi-pdf.gif';
import walletImg from 'wmic-amp-digital-theme-styles/images/policy/poi-wallet.gif';
import messages from './WMICInsuranceCardModal.messages';
import styles from './WMICInsuranceCardModal.module.scss';
import WMICIDCardOption from './WMICIDCardOption';

const WMICInsuranceCardModal = ({
    isOpen,
    onResolve,
    onReject,
    documents,
    walletPassAvailable,
    authHeader,
    originPage
}) => {
    const translator = useTranslator();
    const [emailSent, setEmailSent] = useState(false);
    const [isSending, setIsSending] = useState(false);

    const isDetailsOrigin = originPage === 'details';
    const pageString = isDetailsOrigin
        ? 'my account:insurance:auto policy:auto insurance card'
        : 'my account:manage my account:auto insurance card';

    useEffect(() => {
        // "Auto Insurance Card" modal displayed after clicking the Insurance ID Card CTA on the Home or auto policy Details page.
        publish(WMICTopics.ROUTE_CHANGED, {
            channel: 'my account',
            page: pageString,
            subchannel1: isDetailsOrigin ? 'insurance' : 'manage my account',
            ...(isDetailsOrigin && { subchannel2: 'auto policy' }),
            sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '',
            state: (window.wins_data_layer?.state || '').toString().toLowerCase()
        });
    }, []);

    const onDownloadPdfClick = () => {
        // "Download a PDF version" CTA clicked on the Auto Insurance Card modal.
        publish(WMICTopics.CTA_CLICK, {
            channel: 'my account',
            cta: `${pageString}-download pdf`,
            page: pageString,
            subchannel1: isDetailsOrigin ? 'insurance' : 'manage my account',
            ...(isDetailsOrigin && { subchannel2: 'auto policy' }),
            sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '',
            state: (window.wins_data_layer?.state || '').toString().toLowerCase()
        });
    };

    const onSendViaEmail = () => {
        if (isSending) {
            return;
        }
        setIsSending(true);
        const request = {
            docId: documents.proofOfInsuranceDocument ? documents.proofOfInsuranceDocument.id : undefined,
            token: documents.proofOfInsuranceDocument ? documents.proofOfInsuranceDocument.sessionID : undefined
        };

        PolicyService.sendDocumentViaEmail_WMIC(request, authHeader)
            .then((response = { failReasons: [] }) => {
                setIsSending(false);
                if (response.failReasons && response.failReasons.length > 0) {
                    onResolve(CONSTANTS.ERROR);
                } else {
                    setEmailSent(true);
                }
            }).catch(() => {
                setIsSending(false);
                onResolve(CONSTANTS.ERROR);
            });
    };

    const handleClose = () => {
        onReject();
    };

    return (
        <ModalNext
            isOpen={isOpen}
            className={styles.appropriateDesktopWidth}
            onRequestClose={handleClose} 
            shouldCloseOnEsc
            overlayClassName={styles['ww-payment-modal-overlay']}
            contentLayout={{
                component: 'Grid',
                componentProps: {
                    rows: ['auto'],
                },
            }}
        >
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h3>{translator(messages.insuranceIDCard)}</h3>
                    <p>{translator(messages.chooseHowToReceiveID)}</p>
                </div>
                <Flex
                    wrap={false}
                    className={styles.flexContent}
                >
                    {walletPassAvailable &&
                    <>
                        <FlexItem className={styles.optionSection} shrink={1}>
                            <WMICIDCardOption
                                icon={<img src={walletImg} alt="wallet" />}
                                title={messages.digitalWallet}
                                subtitle={messages.digitalWalletDescription}
                                buttons={[
                                    {
                                        onClick: () => {
                                            onResolve(CONSTANTS.DEVICE_TYPES.IOS);
                                        },
                                        children: messages.iosCustomers,
                                    },
                                    {
                                        onClick: () => {
                                            onResolve(
                                                CONSTANTS.DEVICE_TYPES.ANDROID
                                            );
                                        },
                                        children: messages.androidCustomers,
                                    },
                                ]}
                            />
                        </FlexItem>
                        <FlexItem className={styles.wwPoiDivider}>
                            <div>{translator(messages.or)}</div>
                        </FlexItem>
                    </>
                    }
                    <FlexItem className={cx(styles.optionSection, !walletPassAvailable && styles.onlyPdfToDisplay)} shrink={1}>
                        <WMICIDCardOption
                            icon={<img src={pdfImg} alt="pdf" />}
                            title={messages.pdfFileFormat}
                            subtitle={messages.pdfDescription}
                            emailSent={emailSent}
                            buttons={[
                                {
                                    children: (
                                        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                                        <span onClick={onDownloadPdfClick}>
                                            <WMICDocumentDownloadButton
                                                size="medium"
                                                targetDocument={
                                                    documents.proofOfInsuranceDocument
                                                }
                                            />
                                        </span>
                                    ),
                                },
                                {
                                    onClick: onSendViaEmail,
                                    children: messages.sendCopyViaEmail,
                                    isAnimated: isSending,
                                },
                            ]}
                        />
                    </FlexItem>
                </Flex>
                <ModalFooter>
                    <WMICButton
                        type="primary"
                        className="ww-btn-primary"
                        onClick={() => onResolve()}
                    >
                        {translator(messages.close)}
                    </WMICButton>
                </ModalFooter>
            </div>
        </ModalNext>
    );
};

WMICInsuranceCardModal.propTypes = {
    isOpen: PropTypes.bool,
    onResolve: PropTypes.func,
    documents: PropTypes.arrayOf(PropTypes.shape({})),
    originPage: PropTypes.string
};

WMICInsuranceCardModal.defaultProps = {
    isOpen: true,
    onResolve: () => {},
    documents: [],
    originPage: 'home'
};

export default WMICInsuranceCardModal;
